/**
 * Compact badge for one delegation permission. Used in delegate list rows
 * (admin modal + owner management page).
 */

import { MessageSquare, Mail, Briefcase, UserCog } from 'lucide-react';
import {
  PERMISSION_LABELS,
  type DelegationPermission,
} from './types';

const PERMISSION_ICONS: Record<DelegationPermission, typeof MessageSquare> = {
  MESSAGING: MessageSquare,
  EMAIL_NOTIFICATIONS: Mail,
  WORKSPACE_OPERATIONS: Briefcase,
  PROFILE_MANAGEMENT: UserCog,
};

type PermissionBadgeProps = {
  permission: DelegationPermission;
};

export function PermissionBadge({ permission }: PermissionBadgeProps) {
  const Icon = PERMISSION_ICONS[permission];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-[#026857]/10 px-2 py-0.5 text-[11px] font-medium text-[#026857]"
      data-testid={`permission-badge-${permission}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {PERMISSION_LABELS[permission]}
    </span>
  );
}

type PermissionBadgeListProps = {
  permissions: DelegationPermission[];
};

export function PermissionBadgeList({ permissions }: PermissionBadgeListProps) {
  if (permissions.length === 0) {
    return <span className="text-xs text-gray-400">No permissions</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {permissions.map((permission) => (
        <PermissionBadge key={permission} permission={permission} />
      ))}
    </span>
  );
}
